import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { MDText } from 'i18n-react';
import type SwiperInstance from 'swiper';
import { useSwiper } from 'swiper/react';

import { useGalleryData } from '../Gallery.utils';

import { additionalImageKey, TABS } from './Tabs.const';
import LocalTexts from './Tabs.text';

import styles from './Tabs.module.scss';

const LocalT = new MDText(LocalTexts);

export interface TabEvents {
	onTabClick?: (name: string) => void;
}

interface TabProps {
	category: string;
	events?: TabEvents;
	onEmptyTabClick: (category: string) => void;
}

export const Tabs: FC<TabProps> = ({ category, events, onEmptyTabClick }) => {
	const swiper = useSwiper();
	const { categories, images } = useGalleryData();
	const [active, setActive] = useState(0);

	useEffect(() => {
		swiper.on('activeIndexChange', (sw: SwiperInstance) => {
			setActive(sw.activeIndex);
		});
		return () => {
			swiper.off('activeIndexChange');
		};
	}, [swiper]);

	const onTabCLick = (name: string) => {
		if (categories[name]) {
			const idx = images.findIndex((it) => it.category === name);
			swiper.slideTo(idx, 0);
			onEmptyTabClick('');
		} else {
			onEmptyTabClick(name);
		}
		events?.onTabClick?.(name);
	};

	const filteredTabs = TABS.filter((tab) => {
		return tab.key !== additionalImageKey || images?.some((img) => img.category === tab.key);
	});

	return (
		<div className={styles.tabs}>
			{filteredTabs.map(({ Icon, key }) => {
				const isActive = key === (category || images?.[active]?.category);
				return (
					<button
						key={key}
						aria-label={key}
						className={cx(styles.tab, { [styles.active]: isActive })}
						data-active-tab={isActive}
						onClick={() => onTabCLick(key)}
					>
						<Icon />
						<span className={styles.label}>{LocalT.translate(key)}</span>
						<span className={styles.qty}>{categories?.[key] || 0}</span>
					</button>
				);
			})}
		</div>
	);
};
